import type { HelpArticle } from "../../types";
import { WALL_ASSEMBLY_HELP_FIXTURE } from "../../../calculations/calcPreviewFixtures";

const wall = WALL_ASSEMBLY_HELP_FIXTURE;
const catalogLayer = wall.resolvedLayers[0];
const manualLayer = wall.resolvedLayers[1];
const manualBaseWeight = wall.storedLayers[1].manualWeightPsf!;

export const wallAssemblyArticle: HelpArticle = {
  id: "wall-assembly",
  title: "Building a wall assembly",
  description:
    "Build a wall-face weight from catalog products and explicit manual layers, preserve each layer's identity, and verify the server-resolved line load.",
  tags: ["wall gravity", "assembly", "catalog", "layers", "coverage", "line load"],
  lastUpdated: "July 20, 2026",
  published: false,
  blocks: [
    {
      kind: "lead",
      text: "A wall assembly replaces one opaque face-weight input with a layer ledger. Catalog layers retain stable product keys; manual layers retain their engineer-supplied names and weights. Coverage multiplies each layer, the API resolves catalog weights again before every calculation, and the wall-gravity engine sums the resolved ledger before converting face weight to line load.",
    },

    { kind: "heading", level: 2, text: "Open the assembly builder", id: "open-builder" },
    { kind: "where", path: ["Calculations", "Project", "Wall gravity sheet", "Wall & regions", "Build assembly"] },
    {
      kind: "paragraph",
      text: "Wall gravity starts with an explicit manual face weight. **Build assembly** opens the product-and-layer ledger; after an assembly is applied, the card changes to **Edit assembly** and reports its layer count and face weight. **Clear** removes the ledger and returns control to the explicit manual face-weight field.",
    },
    {
      kind: "shot",
      shotId: "wall-assembly-entry",
      caption: `${wall.mark} with its applied two-layer ledger: Edit assembly, Clear, and the resolved ${wall.faceWeightPsf.toFixed(3)} psf face weight remain visible beside the wall inputs.`,
    },
    {
      kind: "callout",
      variant: "note",
      title: "Layer role is record metadata",
      body: "Interior finish, exterior finish, wall sheathing, insulation, framing, WRB / air barrier, cladding, and other describe each ledger entry. Selecting a role does not establish product compatibility, fire performance, moisture behavior, or structural suitability.",
    },

    { kind: "heading", level: 2, text: "Add catalog and manual layers", id: "add-layers" },
    {
      kind: "steps",
      steps: [
        {
          title: "Find an eligible catalog product",
          body: "Search by product, manufacturer, category, or designation. Only products with a cataloged lb/ft² value and dead-load eligibility appear; decorative or acoustic entries without a structural weight basis are excluded from this picker.",
        },
        {
          title: "Add the product as a layer",
          body: "The row shows its product identity, suggested layer role, manufacturer, and catalog face weight before you add it. The stored assembly keeps the product's catalog key and coverage, not a browser-authored catalog weight.",
        },
        {
          title: "Add explicit allowances separately",
          body: "Use **Add manual layer** for an engineer-supplied item that is not represented by an eligible product. Give it a recognizable name, select its role, and enter its base weight in psf. Manual identity and manual weight remain explicit in the stored ledger.",
        },
        {
          title: "Set Coverage / layers",
          body: "Coverage multiplies that row's base face weight. Use 1 for one full layer, 2 for two equivalent layers, or another project-supported multiplier. The row shows the multiplied layer total, and the header shows the sum of all rows.",
        },
        {
          title: "Apply the ledger",
          body: "**Use assembly** is enabled only when the ledger has at least one layer and a positive preview total. Applying it returns the preview to the wall sheet and stores the layer objects with the inputs.",
        },
      ],
    },
    {
      kind: "shot",
      shotId: "wall-assembly-catalog",
      caption: "The eligible wall-product catalog: identity and source-facing metadata at left, catalog face weight and Add layer action at right.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "The browser total is a preview",
      body: "The API does not trust a total authored by the browser. Before calculation it resolves every stored catalog key, builds a resolved layer ledger, and replaces the wall face-weight input with the server total. A missing or invalid product key must be corrected; it is not silently converted to a manual layer.",
    },

    { kind: "heading", level: 2, text: "Audit the layer math", id: "audit-ledger" },
    {
      kind: "paragraph",
      text: `${wall.mark} provides a fixed two-layer example. The catalog row is **${catalogLayer.product}** at ${catalogLayer.weight.toFixed(3)} psf with coverage ${catalogLayer.coverageFactor}. The manual row is **${manualLayer.product}** at ${manualBaseWeight.toFixed(3)} psf with coverage ${manualLayer.coverageFactor}, so its layer total is ${manualLayer.weight.toFixed(3)} psf. Together they produce ${wall.faceWeightPsf.toFixed(3)} psf.`,
    },
    {
      kind: "formula",
      expression: `(${catalogLayer.weight.toFixed(3)} × ${catalogLayer.coverageFactor}) + (${manualBaseWeight.toFixed(3)} × ${manualLayer.coverageFactor}) = ${wall.faceWeightPsf.toFixed(3)} psf`,
      caption: "WG-1's resolved face weight: one catalog layer plus a three-coverage manual allowance.",
    },
    {
      kind: "values",
      caption: "The identities and contributions preserved in the resolved ledger.",
      rows: [
        { label: `${catalogLayer.role} · ${catalogLayer.product}`, value: `${catalogLayer.weight.toFixed(3)} psf · ${catalogLayer.source}` },
        { label: `${manualLayer.role} · ${manualLayer.product}`, value: `${manualLayer.weight.toFixed(3)} psf · ${manualLayer.source}` },
        { label: "Resolved assembly face weight", value: `${wall.faceWeightPsf.toFixed(3)} psf` },
      ],
    },
    {
      kind: "shot",
      shotId: "wall-assembly-ledger",
      caption: `${wall.mark}'s two rows expose role, product or manual identity, base weight, coverage, layer total, and the ${wall.faceWeightPsf.toFixed(3)} psf resolved preview.`,
    },
    {
      kind: "paragraph",
      text: "The product key is the durable catalog identity. The server resolves that key to the current eligible product entry each time the wall calculation is prepared. That makes a later calculation responsive to catalog corrections while keeping the selected product identifiable. A manual row follows a different path: its name and supplied weight are the source, so the record labels it engineer manual input rather than catalog data.",
    },

    { kind: "heading", level: 2, text: "Verify the wall result", id: "verify-result" },
    {
      kind: "paragraph",
      text: `${wall.mark} applies the ${wall.faceWeightPsf.toFixed(3)} psf assembly to a uniform wall ${wall.inputs.heightLeft_ft} ft high. With no opening deduction, the engine reports ${wall.lineLoadPlf.toFixed(2)} plf at the top, base, and average. The visible trace records ${wall.result.intermediates["Gross wall-face area"]}, ${wall.result.intermediates["Net wall weight"]}, and the policy-limit range. Reconcile those values with the applied assembly card, wall schedule, and Sources disclosure.`,
    },
    {
      kind: "formula",
      expression: `${wall.faceWeightPsf.toFixed(3)} psf × ${wall.inputs.heightLeft_ft} ft = ${wall.lineLoadPlf.toFixed(2)} plf`,
      caption: "The uniform, no-opening WG-1 conversion from face weight to line load.",
    },
    {
      kind: "shot",
      shotId: "wall-assembly-result",
      caption: `${wall.mark}'s server summary reports ${wall.lineLoadPlf.toFixed(2)} plf governing without relying on the explanatory geometry panel.`,
    },
    {
      kind: "shot",
      shotId: "wall-assembly-record",
      caption: `${wall.mark}'s trace, wall schedule, assumptions, and sources reconcile the ${wall.lineLoadPlf.toFixed(2)} plf record while excluding the inaccurate zero-opening schematic.`,
    },
    {
      kind: "paragraph",
      text: "For tapered walls or walls with openings, review the engine's top, base, and average line-load rows and its opening assumption instead of extending the uniform shortcut. The current result states that opening area is distributed proportionally when local opening coordinates are not supplied. That assumption belongs with any value taken from the record.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Two result fields are not faithfully surfaced",
      body: "The engine returns **Wall assembly face weight**, but the wall template omits that intermediate from the visible trace. Separately, the explanatory geometry panel draws an opening even when the stored opening area is zero. The figures here exclude that false schematic. Both defects are publication blockers; use the ledger, numeric trace, schedule, and sources instead.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Keep the provenance split visible",
      body: "Catalog rows and engineer manual rows can coexist, but their sources are not interchangeable. Review the resolved assembly ledger and Sources disclosure after every recalculation; do not cite the catalog as the source of a manual allowance. The current Sources list also includes internal implementation and catalog identifiers rather than a uniformly portable citation set, so provenance remains a publication blocker.",
    },

    { kind: "related", topics: ["load-calcs", "load-calc-results"] },
  ],
};
